// Run-history drawer for ingest automations (SharePoint, Hosted PDF).
// Reads PRD-810 backend (GET /api/v1/ingest-automations/{id}/runs).
//
// Live updates: polls every 8 s when any visible run is in 'running' status.
// Polling pauses when the tab is hidden. Does NOT subscribe to useStatusStream
// (R12 fires on document state transitions, not connector runs).

import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReviewRequiredBanner from '../shared/ReviewRequiredBanner';
import Chip from '../shared/Chip';
import Timestamp from '../shared/Timestamp';
import { formatDuration } from '../../../utils/runDuration';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { apiService } from '../../../services/api';
import './RunHistoryDrawer.css';

// Run status -> Figma chip variant (PRD-1361). Anything else stays Standard.
const RUN_STATUS_VARIANT = {
  success: 'success',
  failed: 'error',
  partial: 'alert',
  running: 'processing',
};
const runStatusVariant = (status) => RUN_STATUS_VARIANT[String(status || '').toLowerCase()] || 'standard';

// Render a report run's delivered table (summary.report_table: list of objects)
// as a simple HTML table. Column order = first-seen across rows.
function ReportTable({ rows }) {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  const columns = [];
  rows.forEach((r) => {
    if (r && typeof r === 'object') {
      Object.keys(r).forEach((k) => {
        if (!columns.includes(k)) columns.push(k);
      });
    }
  });
  if (columns.length === 0) return null;
  return (
    <div className="run-history-report-table-wrap">
      <table className="run-history-report-table">
        <thead>
          <tr>{columns.map((c) => <th key={c}>{c}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>{columns.map((c) => <td key={c}>{r?.[c] == null ? '' : String(r[c])}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const STATUS_FILTERS = [
  { value: '', label: 'All' },
  { value: 'success', label: 'Success' },
  { value: 'failed', label: 'Failed' },
  { value: 'partial', label: 'Partial' },
  { value: 'running', label: 'Running' },
];

const POLL_INTERVAL_MS = 8000;

function isoDateNDaysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function formatTriggerLabel(run) {
  return run.trigger_type === 'manual' ? 'Manual' : 'Scheduled';
}

const RunHistoryDrawer = ({
  automation, isOpen, onClose, onShowNotification, focusRunId = '', reviewDatasets = [],
}) => {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('');
  const [fromDate, setFromDate] = useState(isoDateNDaysAgo(30));
  const [toDate, setToDate] = useState(todayIso());
  // The specific run a report-email deep-link points at (PRD-1269). Fetched
  // on its own so it shows regardless of the date/status filters, and so a
  // recipient without run-history access gets a clear permission message.
  const [focusRun, setFocusRun] = useState(null);
  const [focusError, setFocusError] = useState(null);

  const pollTimerRef = useRef(null);

  const load = useCallback(async () => {
    if (!automation?.id) return;
    setLoading(true);
    setError(null);
    try {
      const params = { page: 1, page_size: 50 };
      if (status) params.status = status;
      if (fromDate) params.from_date = fromDate;
      // Expand to end-of-day so runs later in the day on the selected `to` date
      // are included. Backend parses bare `YYYY-MM-DD` as midnight, which would
      // otherwise exclude everything that happened after 00:00 on that date.
      if (toDate) params.to_date = `${toDate}T23:59:59`;
      const resp = await apiService.listIngestRuns(automation.id, params);
      const items = resp?.items ?? (Array.isArray(resp) ? resp : []);
      setRuns(items);
    } catch (err) {
      const msg = err?.message || 'Failed to load run history';
      setError(msg);
      setRuns([]);
    } finally {
      setLoading(false);
    }
  }, [automation, status, fromDate, toDate]);

  useEffect(() => {
    if (isOpen) load();
  }, [isOpen, load]);

  // Fetch the deep-linked run on its own so it always renders (bypasses the
  // date/status filters) and so a 403 surfaces as a clear permission message.
  useEffect(() => {
    if (!isOpen || !focusRunId || !automation?.id) {
      setFocusRun(null);
      setFocusError(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const run = await apiService.getIngestRun(automation.id, focusRunId);
        if (!cancelled) {
          setFocusRun(run);
          setFocusError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setFocusRun(null);
          setFocusError(
            err?.status === 403
              ? 'You do not have permission to view this report.'
              : err?.status === 404
                ? 'This report run could not be found.'
                : err?.message || 'Failed to load this report.',
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, focusRunId, automation]);

  // Polling: refetch every POLL_INTERVAL_MS while at least one run is 'running'.
  // Paused while the tab is hidden so we don't burn API quota in background tabs.
  useEffect(() => {
    if (!isOpen) return undefined;
    const hasRunning = runs.some((r) => r.status === 'running');
    if (!hasRunning) {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      return undefined;
    }
    if (pollTimerRef.current) return undefined;
    pollTimerRef.current = setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      load();
    }, POLL_INTERVAL_MS);
    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [isOpen, runs, load]);

  if (!isOpen) return null;

  return (
    <div className="run-history-drawer" data-testid="run-history-drawer">
      <div className="run-history-drawer-overlay" onClick={onClose} />
      <div className="run-history-drawer-panel">
        <div className="run-history-drawer-header">
          <h3>{automation?.name || 'Automation'} — run history</h3>
          <button
            className="run-history-drawer-close"
            onClick={onClose}
            aria-label="Close"
            type="button"
          >
            &times;
          </button>
        </div>

        {/* PRD-1359 (Figma "Automations - Automation"): the same warning the
            wizard shows, here because this is where an author reads a run's
            outcome — a run over a dataset with parked documents is incomplete,
            and the history should say so before the numbers are trusted. */}
        <ReviewRequiredBanner
          datasets={reviewDatasets}
          variant="automation"
          className="run-history-review-banner"
        />

        <div className="run-history-filters">
          <div className="run-history-status-chips" role="group" aria-label="Filter by status">
            {STATUS_FILTERS.map((s) => (
              <button
                key={s.value || 'all'}
                type="button"
                className={`run-history-chip ${status === s.value ? 'is-active' : ''}`}
                onClick={() => setStatus(s.value)}
              >
                {s.label}
              </button>
            ))}
          </div>
          <div className="run-history-date-range">
            <label className="run-history-date-label">
              From
              <input
                type="date"
                className="resui-input resui-input--compact"
                value={fromDate}
                max={toDate || undefined}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </label>
            <label className="run-history-date-label">
              To
              <input
                type="date"
                className="resui-input resui-input--compact"
                value={toDate}
                min={fromDate || undefined}
                onChange={(e) => setToDate(e.target.value)}
              />
            </label>
          </div>
        </div>

        <div className="run-history-drawer-body">
          {(focusError || focusRun) && (
            <div className="run-history-report-panel">
              <h4 className="run-history-report-panel-title">Delivered report</h4>
              {focusError ? (
                <div className="push-step-error">{focusError}</div>
              ) : (
                (() => {
                  const s = focusRun.summary || {};
                  const responseText = s.response_text;
                  const table = s.report_table;
                  // A failed run (incl. "generated but delivery failed") stores no
                  // response — surface its reason, which is the point of PRD-1269,
                  // instead of the generic "no delivered response" line.
                  if (focusRun.status && focusRun.status !== 'success') {
                    return (
                      <>
                        <Chip
                          variant={runStatusVariant(focusRun.status)}
                          className="run-history-status"
                        >
                          {focusRun.status}
                        </Chip>
                        {focusRun.error && <p className="run-history-report-error">{focusRun.error}</p>}
                      </>
                    );
                  }
                  if (!responseText && !(Array.isArray(table) && table.length > 0)) {
                    return <p>This run did not store a delivered response.</p>;
                  }
                  return (
                    <>
                      {responseText && (
                        <div className="run-history-report-body">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>{responseText}</ReactMarkdown>
                        </div>
                      )}
                      <ReportTable rows={table} />
                    </>
                  );
                })()
              )}
            </div>
          )}
          {loading ? (
            <div className="push-step-error">Loading runs...</div>
          ) : error && !focusError ? (
            // On a deep-link 403 both fetches fail; the panel already shows the
            // permission message, so don't also render the raw list error under it.
            <div className="push-step-error">{error}</div>
          ) : runs.length === 0 ? (
            <p>No runs yet.</p>
          ) : (
            <ul className="run-history-list">
              {runs.map((r) => {
                const s = r.summary || {};
                const artifacts = Array.isArray(s.artifacts) ? s.artifacts.length : s.artifacts;
                const datasetFiles = Array.isArray(s.dataset_file_ids)
                  ? s.dataset_file_ids.length
                  : s.dataset_file_ids;
                const extractionJobs = Array.isArray(s.extraction_job_ids)
                  ? s.extraction_job_ids.length
                  : s.extraction_job_ids;
                const skipped = Array.isArray(s.skipped_duplicates)
                  ? s.skipped_duplicates.length
                  : s.skipped_duplicates;
                const ingested = s.records_ingested ?? artifacts ?? datasetFiles;
                const failed = s.records_failed;
                const metrics = [];
                if (ingested != null) metrics.push(`New files ingested: ${ingested}`);
                if (datasetFiles != null && datasetFiles !== ingested)
                  metrics.push(`Dataset files: ${datasetFiles}`);
                if (extractionJobs != null) metrics.push(`Extraction jobs: ${extractionJobs}`);
                if (skipped != null) metrics.push(`Skipped duplicates: ${skipped}`);
                if (failed != null) metrics.push(`Failed: ${failed}`);
                return (
                  <li key={r.id} className="run-history-item">
                    <div className="run-history-item-row">
                      <Chip
                        variant={runStatusVariant(r.status)}
                        className="run-history-status"
                      >
                        {r.status || 'unknown'}
                      </Chip>
                      <Chip className="run-history-trigger">{formatTriggerLabel(r)}</Chip>
                      <span className="run-history-time">
                        <Timestamp value={r.started_at} />
                      </span>
                      <span className="run-history-meta">
                        Duration: {formatDuration(r.started_at, r.completed_at)}
                      </span>
                    </div>
                    {metrics.length > 0 && (
                      <div className="run-history-meta">{metrics.join(' · ')}</div>
                    )}
                    {Array.isArray(s.artifacts) && s.artifacts.length > 0 && (
                      <details className="run-history-artifacts">
                        <summary>View ingested files ({s.artifacts.length})</summary>
                        <ul className="run-history-artifact-list">
                          {s.artifacts.slice(0, 50).map((a, idx) => {
                            const name =
                              typeof a === 'string'
                                ? a
                                : a?.filename || a?.name || a?.path || a?.url || JSON.stringify(a);
                            return <li key={idx}>{name}</li>;
                          })}
                          {s.artifacts.length > 50 && (
                            <li>… and {s.artifacts.length - 50} more</li>
                          )}
                        </ul>
                      </details>
                    )}
                    {r.error && <div className="run-history-error">{r.error}</div>}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default RunHistoryDrawer;
